import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ScrollToTop from '../components/ScrollToTop';
import '../styles/BlogsPage.css';

const faqData = [
  {
    question: 'What is MeetingPoint?',
    answer: 'MeetingPoint is a browser-based, peer-to-peer (P2P) communication tool designed for instant, accountless video and voice calls. We prioritize user privacy by removing the central server from the conversation. Your media streams flow directly between your device and your peer\'s device using WebRTC technology.'
  },
  {
    question: 'Do I need to create an account?',
    answer: 'No. We believe in "Zero-Identity" utility. You can start a session and invite a peer without ever giving us an email address, phone number, or name. Simply click "Start Meeting," share the link, and you\'re connected.'
  },
  {
    question: 'Is my video or audio recorded?',
    answer: 'Never. Because our architecture is P2P, your media stream goes directly from your device to your peer\'s device. It never touches our servers, so there is nothing for us to record or store. When you close the tab, the connection is severed and nothing remains.'
  },
  {
    question: 'How does the "Link Sharing" work?',
    answer: 'When you generate a meeting link, our signaling server helps your browser find your friend\'s browser. Once the "digital handshake" is complete, the signaling server steps out, and a direct, encrypted tunnel is formed between you. The link is a temporary identifier — it is not tied to any personal data.'
  },
  {
    question: 'Does MeetingPoint work on mobile?',
    answer: 'Yes. MeetingPoint is fully responsive and works on any modern mobile browser (Chrome, Safari, Firefox) that supports WebRTC standards. No app download is required — if you can open a webpage, you can start a meeting.'
  },
  {
    question: 'Why is my connection slow?',
    answer: 'In a P2P setup, the quality of the call depends on the upload/download speeds of both participants. If you are experiencing lag, check your local connection or try disabling any high-bandwidth background apps. For the best experience, use a stable Wi-Fi or wired Ethernet connection.'
  },
  {
    question: 'Is the service really free?',
    answer: 'Yes. We use minimal non-intrusive advertising on our informational pages to cover the costs of our signaling servers and domain maintenance. We will never "sell" your data because we don\'t collect it in the first place.'
  },
  {
    question: 'Can my peer see my IP address?',
    answer: 'In a P2P connection, your IP address is inherently visible to the person you are directly connecting with — this is how direct connections work. If you want to hide your physical location from your peer, we recommend using a VPN before joining the session.'
  },
  {
    question: 'How many people can join a call?',
    answer: 'MeetingPoint supports group calls with up to 4 participants. You can also use text chat, file sharing, and screen sharing alongside video and audio calls.'
  },
  {
    question: 'What browsers are supported?',
    answer: 'MeetingPoint works in any modern browser that supports WebRTC, including Google Chrome, Mozilla Firefox, Microsoft Edge, Safari, Brave, and Opera. We recommend keeping your browser updated for the best experience and security.'
  }
];

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="blogs-page">
      {/* Navbar */}
      <nav className="navbar navbar-expand-lg blogs-navbar">
        <div className="container">
          <Link className="navbar-brand" to="/">
            <img src="/images/sidebar-logo.png" alt="MeetingPoint Logo" loading="lazy" />
          </Link>
          <div className="d-flex align-items-center gap-3">
            <Link to="/blogs" className="btn btn-outline-light btn-sm rounded-pill px-3">
              <i className="fas fa-newspaper me-2"></i>Blog
            </Link>
            <Link to="/" className="btn btn-outline-light btn-sm rounded-pill px-3">
              <i className="fas fa-arrow-left me-2"></i>Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="blog-detail-hero">
        <div className="container">
          <h1 className="blog-detail-title">Frequently Asked Questions</h1>
          <p className="blog-detail-excerpt">All your burning questions, minus the corporate fluff.</p>
        </div>
      </header>

      {/* Content */}
      <article className="container blog-detail-content">
        <div className="blog-detail-body privacy-body">
          <p>
            Got questions about how MeetingPoint works, our privacy architecture, or troubleshooting tips? Here are the answers to the most common questions we receive.
          </p>

          <div style={{ marginTop: '2rem' }}>
            {faqData.map((faq, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '10px',
                  marginBottom: '1rem',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease'
                }}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  style={{
                    width: '100%',
                    padding: '1.25rem 1.5rem',
                    background: openIndex === index ? 'rgba(255, 85, 116, 0.08)' : 'rgba(255, 255, 255, 0.04)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    textAlign: 'left',
                    fontSize: '1.05rem',
                    fontWeight: '600',
                    color: '#e0e0e0',
                    transition: 'background 0.3s ease'
                  }}
                >
                  <span>{faq.question}</span>
                  <i
                    className={`fas fa-chevron-${openIndex === index ? 'up' : 'down'}`}
                    style={{ color: '#ff5574', fontSize: '0.85rem', marginLeft: '1rem', flexShrink: 0 }}
                  ></i>
                </button>
                {openIndex === index && (
                  <div
                    style={{
                      padding: '0 1.5rem 1.25rem 1.5rem',
                      background: 'rgba(255, 85, 116, 0.06)',
                      color: '#bbb',
                      lineHeight: '1.7',
                      fontSize: '0.95rem'
                    }}
                  >
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{ marginTop: '3rem', padding: '2rem', background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '12px', textAlign: 'center' }}>
            <h3 style={{ marginBottom: '1rem', color: '#fff' }}>Still Have Questions?</h3>
            <p style={{ marginBottom: '1.5rem', color: '#aaa' }}>
              Can't find what you're looking for? Our team is happy to help.
            </p>
            <Link
              to="/contact"
              style={{
                display: 'inline-block',
                padding: '0.75rem 2rem',
                background: 'linear-gradient(135deg, #ff5574, #ff8a9e)',
                color: '#fff',
                borderRadius: '50px',
                textDecoration: 'none',
                fontWeight: '600',
                transition: 'transform 0.2s ease'
              }}
            >
              Contact Us <i className="fas fa-arrow-right ms-2"></i>
            </Link>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="blogs-footer">
        <div className="container text-center">
          <p className="mb-1">&copy; {new Date().getFullYear()} MeetingPoint. All rights reserved.</p>
          <div className="d-flex justify-content-center gap-3 mt-2 flex-wrap">
            <Link to="/" className="text-muted">Home</Link>
            <Link to="/about" className="text-muted">About</Link>
            <Link to="/blogs" className="text-muted">Blog</Link>
            <Link to="/contact" className="text-muted">Contact</Link>
            <Link to="/privacy" className="text-muted">Privacy Policy</Link>
            <Link to="/terms" className="text-muted">Terms</Link>
            <Link to="/disclaimer" className="text-muted">Disclaimer</Link>
          </div>
        </div>
      </footer>
      <ScrollToTop />
    </div>
  );
}
